import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

type Props = {
  tsCodes: string[];
  onClose: () => void;
  onDone?: () => void;
};

function cleanCodes(values: string[]): string[] {
  return [...new Set(values.map((value) => value.trim().toUpperCase()).filter(Boolean))];
}

function cleanTags(value: string): string[] {
  return [...new Set(value.split(/[,，\n]+/).map((item) => item.trim()).filter(Boolean))];
}

export default function StockOrganizerDialog({ tsCodes, onClose, onDone }: Props) {
  const qc = useQueryClient();
  const codes = useMemo(() => cleanCodes(tsCodes), [tsCodes]);
  const [groupName, setGroupName] = useState("");
  const [tagText, setTagText] = useState("");
  const [basketId, setBasketId] = useState("");
  const [newBasketName, setNewBasketName] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const watchQ = useQuery({ queryKey: ["watchlist"], queryFn: () => api.listWatchlist() });
  const groupsQ = useQuery({ queryKey: ["watchlist-groups"], queryFn: api.listWatchlistGroups });
  const tagsQ = useQuery({ queryKey: ["watchlist-tags"], queryFn: api.listWatchlistTags });
  const basketsQ = useQuery({ queryKey: ["baskets"], queryFn: api.listBaskets });
  const watched = useMemo(
    () => new Map((watchQ.data ?? []).map((item) => [item.ts_code.toUpperCase(), item])),
    [watchQ.data],
  );
  const singleItem = codes.length === 1 ? watched.get(codes[0]!) : undefined;

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["watchlist"] }),
      qc.invalidateQueries({ queryKey: ["watchlist-quotes"] }),
      qc.invalidateQueries({ queryKey: ["watchlist-groups"] }),
      qc.invalidateQueries({ queryKey: ["watchlist-tags"] }),
      qc.invalidateQueries({ queryKey: ["baskets"] }),
    ]);
    onDone?.();
  };

  const run = async (label: string, action: () => Promise<unknown>) => {
    setPending(label);
    setMessage(null);
    try {
      await action();
      await refresh();
      setMessage(`${label}成功`);
    } catch (error) {
      setMessage(`${label}失败：${(error as Error).message}`);
    } finally {
      setPending(null);
    }
  };

  const ensureWatched = async () => {
    await api.addWatchlistBatch(codes);
  };

  const currentSummary = singleItem
    ? `当前分组：${singleItem.group_name}；标签：${singleItem.tags.length ? singleItem.tags.join("、") : "无"}`
    : `${codes.length} 只股票，可统一整理`;

  return <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4" role="dialog" aria-modal="true" aria-labelledby="stock-organizer-title">
    <div className="w-full max-w-xl rounded-xl bg-white p-5 shadow-xl dark:bg-zinc-900">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h2 id="stock-organizer-title" className="text-lg font-medium">整理股票</h2>
          <p className="mt-1 text-xs text-zinc-500">{currentSummary}</p>
        </div>
        <button type="button" onClick={onClose} className="rounded px-2 py-1 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800" aria-label="关闭">✕</button>
      </div>

      <div className="mt-4 max-h-24 overflow-y-auto rounded-lg bg-zinc-50 p-2 dark:bg-zinc-950">
        <div className="flex flex-wrap gap-1.5">
          {codes.map((code) => <span key={code} className="rounded border px-2 py-0.5 font-mono text-xs dark:border-zinc-700">{code}</span>)}
        </div>
      </div>

      <div className="mt-4 space-y-4">
        <section className="rounded-lg border p-3 dark:border-zinc-700">
          <h3 className="text-sm font-medium">📁 自选分组</h3>
          <div className="mt-2 flex gap-2">
            <input value={groupName} onChange={(event) => setGroupName(event.target.value)} list="stock-organizer-groups" placeholder="选择或输入新分组"
              className="min-w-0 flex-1 rounded border px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950" />
            <datalist id="stock-organizer-groups">{(groupsQ.data ?? []).map((group) => <option key={group.group_name} value={group.group_name} />)}</datalist>
            <button type="button" disabled={!groupName.trim() || pending != null} onClick={() => run("更新分组", async () => {
              await ensureWatched();
              await api.batchGroupWatchlist(codes, groupName.trim());
            })} className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-40">应用</button>
          </div>
        </section>

        <section className="rounded-lg border p-3 dark:border-zinc-700">
          <h3 className="text-sm font-medium">🏷️ 股票标签</h3>
          <div className="mt-2 flex gap-2">
            <input value={tagText} onChange={(event) => setTagText(event.target.value)} placeholder="输入标签，多个用逗号分隔"
              className="min-w-0 flex-1 rounded border px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950" />
            <button type="button" disabled={!cleanTags(tagText).length || pending != null} onClick={() => run("添加标签", async () => {
              await ensureWatched();
              await api.batchTagsWatchlist(codes, cleanTags(tagText), "add");
            })} className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-40">添加</button>
            <button type="button" disabled={!cleanTags(tagText).length || pending != null} onClick={() => run("移除标签", async () => {
              await api.batchTagsWatchlist(codes, cleanTags(tagText), "remove");
            })} className="rounded border px-3 py-2 text-sm disabled:opacity-40 dark:border-zinc-700">移除</button>
          </div>
          {(tagsQ.data?.length ?? 0) > 0 && <div className="mt-2 flex flex-wrap gap-1">
            {(tagsQ.data ?? []).map((tag) => <button type="button" key={tag.tag} onClick={() => setTagText((current) => cleanTags(`${current},${tag.tag}`).join(", "))}
              className="rounded bg-zinc-100 px-2 py-0.5 text-xs hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700">{tag.tag}</button>)}
          </div>}
        </section>

        <section className="rounded-lg border p-3 dark:border-zinc-700">
          <h3 className="text-sm font-medium">💼 股票池</h3>
          <div className="mt-2 flex gap-2">
            <select value={basketId} onChange={(event) => setBasketId(event.target.value)} className="min-w-0 flex-1 rounded border px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950">
              <option value="">选择已有股票池</option>
              {(basketsQ.data ?? []).map((basket) => <option key={basket.id} value={basket.id}>{basket.name}（{basket.n_stocks}）</option>)}
            </select>
            <button type="button" disabled={!basketId || pending != null} onClick={() => run("加入股票池", async () => {
              const basket = (basketsQ.data ?? []).find((item) => item.id === Number(basketId));
              if (!basket) throw new Error("股票池不存在");
              await api.patchBasket(basket.id, { ts_codes: cleanCodes([...basket.ts_codes, ...codes]) });
            })} className="rounded bg-violet-600 px-3 py-2 text-sm text-white disabled:opacity-40">加入</button>
          </div>
          <div className="mt-2 flex gap-2">
            <input value={newBasketName} onChange={(event) => setNewBasketName(event.target.value)} placeholder="或输入名称创建新股票池"
              className="min-w-0 flex-1 rounded border px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950" />
            <button type="button" disabled={!newBasketName.trim() || pending != null} onClick={() => run("创建股票池", () => api.createBasket({ name: newBasketName.trim(), ts_codes: codes }))}
              className="rounded border px-3 py-2 text-sm disabled:opacity-40 dark:border-zinc-700">新建</button>
          </div>
        </section>
      </div>

      <div className="mt-4 flex items-center gap-3 border-t pt-4 dark:border-zinc-800">
        <span className={`flex-1 text-xs ${message?.includes("失败") ? "text-rose-600" : "text-emerald-600"}`}>{pending ? `${pending}中…` : message}</span>
        <button type="button" onClick={onClose} className="rounded border px-4 py-2 text-sm dark:border-zinc-700">完成</button>
      </div>
    </div>
  </div>;
}
